import type { AgentThreadMessage } from "@/store/chat.types";
import { assembleMessageList } from "@/store/helpers/assembleMessageList";
import { mergeStreamingMessages } from "@/store/helpers/mergeStreamingMessages";
import {
  createOptimisticMessage,
  omitOptimisticMessages,
} from "@/store/helpers/optimisticMessageHelper";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { AgentThreadVoStatus } from "../api";

type SetKeys = keyof Pick<ChatStore, "rawMessages">;

export class ChatStore {
  @observable rawMessages: AgentThreadMessage[] = [];
  @observable threadId: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  @computed
  get latestMsgHash() {
    if (!this.messages || this.messages.length === 0) {
      return "";
    }

    const latestMessage = this.messages[this.messages.length - 1];

    return JSON.stringify(latestMessage);
  }

  @action setThreadId(id: string) {
    if (this.threadId && this.threadId !== id) {
      this.set("rawMessages", []);
    }
    this.threadId = id;
  }

  @action addOptimisticUserMessage(text: string, files: File[]) {
    if (!this.threadId) return;
    const message = createOptimisticMessage(text, files);
    this.rawMessages.push(message);
  }

  @action removeOptimisticUserMessage() {
    this.rawMessages = omitOptimisticMessages(this.rawMessages);
  }

  @action setRawMessages(threadId: string, messages: AgentThreadMessage[]) {
    if (this.threadId && this.threadId !== threadId) {
      return;
    }
    this.rawMessages = omitOptimisticMessages(messages);
  }

  @action setStreamingMessages(
    threadId: string,
    threadStatus: AgentThreadVoStatus,
    messages: AgentThreadMessage[]
  ) {
    if (this.threadId && this.threadId !== threadId) {
      return;
    }

    let filteredMessages = this.rawMessages;

    if (messages.length > 0 && threadStatus !== AgentThreadVoStatus.Pending) {
      filteredMessages = omitOptimisticMessages(this.rawMessages);
    }

    const mergedStreaming = mergeStreamingMessages(filteredMessages, messages);
    this.rawMessages = mergedStreaming;
  }

  @computed
  get messages() {
    return assembleMessageList(this.rawMessages);
  }

  @action set<K extends SetKeys>(k: K, v: ChatStore[K]) {
    this[k] = v as never;
  }

  @action clearThread() {
    this.threadId = null;
    this.set("rawMessages", []);
  }
}

export const chatStore = new ChatStore();

export { isAssistantIteration } from "@/store/chat.types";
export type {
  AgentThreadAssistantIteration,
  AgentThreadMessage,
  AgentThreadMessageContent,
  AgentThreadMessageItem,
} from "@/store/chat.types";
export { assembleMessageList };
